import { useCallback, useEffect, useState } from 'react';

import { listTenantPushUsers, TenantPushUser } from '../PushAPI';

export type PushUsersState = (
    | { status: 'idle'; users: TenantPushUser[] }
    | { status: 'loading'; users: TenantPushUser[] }
    | { status: 'ready'; users: TenantPushUser[] }
    | { status: 'error'; users: TenantPushUser[]; message: string }
) & {
    /** Loads the list again (the previous people stay in `users` meanwhile). Absent where a list is handed in from outside. */
    reload?: () => void;
};

/**
 * The tenant's users with their push state, loaded once while `enabled` and again on `reload()`.
 *
 * Failure is a state, not a modal: the alert editor shows it inline, because the editor must stay
 * usable (and quiet) while the endpoint is missing or the viewer lacks the right for it.
 */
export const usePushUsers = (customer: string | undefined, enabled: boolean): PushUsersState => {
    const [state, setState] = useState<PushUsersState>({ status: 'idle', users: [] });
    const [round, setRound] = useState(0);
    const reload = useCallback(() => setRound((current) => current + 1), []);

    useEffect(() => {
        if (!enabled || !customer) {
            return;
        }

        let cancelled = false;

        setState((previous) => ({ status: 'loading', users: previous.users }));
        listTenantPushUsers(customer)
            .then((users) => {
                if (!cancelled) setState({ status: 'ready', users });
            })
            .catch((error) => {
                if (!cancelled) setState((previous) => ({ status: 'error', users: previous.users, message: String(error?.message ?? error) }));
            });

        return () => {
            cancelled = true;
        };
    }, [customer, enabled, round]);

    return { ...state, reload };
};
